import { useEffect } from "react";
import { useStore } from "effector-react";
import { useGrowthBook } from "@growthbook/growthbook-react";

import { $account } from "../../../entities/account";
import { $serviceLocation } from "../../../entities/locations";
import { $quote } from "../../../entities/quote";

/**
 * Syncs booking flow data to GrowthBook attributes after each store update.
 *
 * GrowthBook uses these attributes to:
 * - Target experiments at specific locations, service categories, or dwelling types
 * - Personalize the workflow variant mid-flow (e.g. change step 3 after step 2 data
 *   is known, such as routing a business-address user differently after pick-up-location)
 *
 * gb.setAttributes() is synchronous, so useFeatureValue("booking-workflow-variant")
 * re-evaluates immediately after each update, potentially changing the variant and
 * triggering a new manifest fetch for the remaining steps.
 */
export const useGrowthBookAttributes = () => {
  const gb = useGrowthBook();
  const { account } = useStore($account);
  const { location } = useStore($serviceLocation);
  const { quote } = useStore($quote);

  const locationId = location?.id;
  const city = account?.billingAddress?.city;
  const state = account?.billingAddress?.state;
  const categoryId = account?.category?.id;
  const targetDate = quote?.date;
  const rooms = quote?.rooms;
  const dwellingTypeId = quote?.origin?.dwellingType?.id;
  const destinationDwellingTypeId = quote?.destination?.dwellingType?.id;

  useEffect(() => {
    if (!gb) return;

    const attrs: Record<string, unknown> = {
      environment: import.meta.env.VITE_ENVIRONMENT,
    };

    if (locationId != null) attrs.locationId = locationId;
    if (city) attrs.city = city;
    if (state) attrs.state = state;
    if (categoryId != null) attrs.categoryId = categoryId;

    if (targetDate) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const d = new Date(targetDate);
      d.setHours(0, 0, 0, 0);
      const days = Math.round(
        (d.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
      );
      if (!isNaN(days)) attrs.daysUntilTargetDate = days;
    }

    if (rooms != null) attrs.rooms = rooms;
    if (dwellingTypeId != null) attrs.dwellingTypeId = dwellingTypeId;
    if (destinationDwellingTypeId != null)
      attrs.destinationDwellingTypeId = destinationDwellingTypeId;

    // Merge with existing attributes to preserve id: visitorId set by provider
    gb.setAttributes({ ...gb.getAttributes(), ...attrs });
  }, [
    gb,
    locationId,
    city,
    state,
    categoryId,
    targetDate,
    rooms,
    dwellingTypeId,
    destinationDwellingTypeId,
  ]);
};
